// Firebase functions for contact management
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase-config";

// Add a fast track contact
export const addFastTrackContact = async (contactData) => {
  try {
    console.log('Adding fast track contact:', contactData);
    
    const auth = getAuth();
    const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
    
    // Create the contact document
    const contact = {
      ...contactData,
      createdAt: serverTimestamp(),
      createdBy: adminEmail,
      type: contactData.type || 'fast_track_connect'
    };
    
    const contactRef = await addDoc(collection(db, 'contacts'), contact);
    console.log('Contact created with ID:', contactRef.id);
    
    // If this contact is associated with a job, update the job's activity feed
    if (contactData.jobId && contactData.userId) {
      const jobRef = doc(db, 'jobs', contactData.jobId);
      const jobDoc = await getDoc(jobRef);
      
      if (jobDoc.exists()) {
        const jobData = jobDoc.data();
        const userReactions = jobData.userReactions || {};
        const userReaction = userReactions[contactData.userId] || {};
        const activities = userReaction.activities || [];
        
        // Create new activity
        const newActivity = {
          type: contactData.type || 'fast_track_connect',
          adminName: adminEmail,
          timestamp: new Date(), // Use JavaScript Date instead of serverTimestamp()
          notes: contactData.notes,
          contactId: contactRef.id,
          contact: {
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email,
            company: contactData.company,
            availableTimes: contactData.availableTimes
          }
        };
        
        // Add new activity to the beginning of the array
        await updateDoc(jobRef, {
          [`userReactions.${contactData.userId}.activities`]: [newActivity, ...activities]
        });
      }
    }
    
    return {
      success: true,
      contactId: contactRef.id,
      contact
    };
  } catch (error) {
    console.error('Error adding fast track contact:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update a fast track contact
export const updateFastTrackContact = async (contactId, updateData) => {
  try {
    console.log('Updating fast track contact:', contactId, updateData);
    
    const auth = getAuth();
    const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
    
    // Get the contact document
    const contactRef = doc(db, 'contacts', contactId);
    const contactDoc = await getDoc(contactRef);
    
    if (!contactDoc.exists()) {
      throw new Error(`Contact with ID ${contactId} not found`);
    }
    
    const contactData = contactDoc.data();
    
    // Update the contact
    await updateDoc(contactRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
      updatedBy: adminEmail
    });
    
    // If this contact is associated with a job, update the activity
    if (contactData.jobId && contactData.userId && updateData.availableTimes) {
      const jobRef = doc(db, 'jobs', contactData.jobId);
      const jobDoc = await getDoc(jobRef);
      
      if (jobDoc.exists()) {
        const jobData = jobDoc.data();
        const userReactions = jobData.userReactions || {};
        const userReaction = userReactions[contactData.userId] || {};
        const activities = userReaction.activities || [];
        
        // Find the activity with this contactId
        const activityIndex = activities.findIndex(a => a.contactId === contactId);
        
        if (activityIndex >= 0) {
          // Update the activity with new contact info
          const updatedActivity = {
            ...activities[activityIndex],
            updatedAt: new Date(), // Use JavaScript Date instead of serverTimestamp
            contact: {
              ...activities[activityIndex].contact,
              ...updateData
            }
          };
          
          const updatedActivities = [...activities];
          updatedActivities[activityIndex] = updatedActivity;
          
          await updateDoc(jobRef, {
            [`userReactions.${contactData.userId}.activities`]: updatedActivities
          });
        }
      }
    }
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error updating fast track contact:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get contacts for a user
export const getUserContacts = async (userId) => {
  try {
    if (!userId) {
      return { success: true, contacts: [] };
    }
    
    console.log('Getting contacts for user:', userId);
    
    const contactsRef = collection(db, 'contacts');
    const q = query(contactsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const contacts = [];
    querySnapshot.forEach((doc) => {
      contacts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      contacts
    };
  } catch (error) {
    console.error('Error getting user contacts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get contacts for a job
export const getJobContacts = async (jobId) => {
  try {
    console.log('Getting contacts for job:', jobId);
    
    const contactsRef = collection(db, 'contacts');
    const q = query(contactsRef, where('jobId', '==', jobId));
    const querySnapshot = await getDocs(q);
    
    const contacts = [];
    querySnapshot.forEach((doc) => {
      contacts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      contacts
    };
  } catch (error) {
    console.error('Error getting job contacts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get contacts for a company
export const getCompanyContacts = async (companyId) => {
  try {
    console.log('Getting contacts for company:', companyId);
    
    const contactsRef = collection(db, 'contacts');
    
    // First try to get contacts that have this company ID in their companyIds array
    const q = query(contactsRef, where('companyIds', 'array-contains', companyId));
    const querySnapshot = await getDocs(q);
    
    const contacts = [];
    querySnapshot.forEach((doc) => {
      contacts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Then try to get contacts that have this company as their company field
    const q2 = query(contactsRef, where('company', '==', companyId));
    const querySnapshot2 = await getDocs(q2);
    
    // Add any contacts that aren't already in the list
    const existingIds = new Set(contacts.map(c => c.id));
    querySnapshot2.forEach((doc) => {
      if (!existingIds.has(doc.id)) {
        contacts.push({
          id: doc.id,
          ...doc.data()
        });
      }
    });
    
    return {
      success: true,
      contacts
    };
  } catch (error) {
    console.error('Error getting company contacts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all contacts - used for admin selection
export const getAllContacts = async (limitCount = 100) => {
  try {
    console.log('Getting all contacts');
    
    const contactsRef = collection(db, 'contacts');
    
    let contacts = [];
    
    // First try with orderBy if possible (for documents with createdAt)
    try {
      const q = query(contactsRef, orderBy('createdAt', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        contacts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Retrieved ${contacts.length} contacts with createdAt timestamp`);
    } catch (orderByError) {
      console.warn('Error with orderBy query, falling back to basic query:', orderByError);
      
      // If the orderBy fails (possibly due to missing fields), fall back to a simple query
      const basicQuery = query(contactsRef, limit(limitCount));
      const basicSnapshot = await getDocs(basicQuery);
      
      contacts = [];
      basicSnapshot.forEach((doc) => {
        contacts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Retrieved ${contacts.length} contacts without ordering`);
    }
    
    // Log some stats to help diagnose issues
    if (contacts.length > 0) {
      const withCreatedAt = contacts.filter(c => c.createdAt).length;
      const withCompanyIds = contacts.filter(c => c.companyIds && c.companyIds.length > 0).length;
      const withFirstName = contacts.filter(c => c.firstName).length;
      const withEmail = contacts.filter(c => c.email).length;
      
      console.log(`Contact stats: ${withCreatedAt}/${contacts.length} have createdAt, ` +
                  `${withCompanyIds}/${contacts.length} have companyIds, ` + 
                  `${withFirstName}/${contacts.length} have firstName, ` +
                  `${withEmail}/${contacts.length} have email`);
    } else {
      console.warn('No contacts found in the database');
    }
    
    return {
      success: true,
      contacts
    };
  } catch (error) {
    console.error('Error getting all contacts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};